import { storeToRefs } from 'pinia'

import { RouteNamedMap } from 'vue-router/auto-routes'

import { ShiftID } from '../../shifts/models/shift'

import { useAuthSiteStore } from '../../user/authSiteStore'
import { EmployeeID } from '../../employees/models/employee'
import { ShiftReasonID } from '../../shift_reasons/models/shiftReason'
import { GroupID } from '../../groups/models/group'
import { TimeOffRequestID } from '../../time-off/models/timeOffRequest'
import { BroadcastID } from '../../broadcasts/models/broadcast'
import { BuildingID } from '../../buildings/models/building'
import { SkillID } from '../../skills/models/skill'

type RouteName = keyof RouteNamedMap

export interface OrgRouteDescriptor<T extends RouteName = RouteName> {
  name: T
  params?: Omit<RouteNamedMap[T]['params'], 'org'>
}

export function useSiteRouter() {
  const authSiteStore = useAuthSiteStore()
  const { currentSlug } = storeToRefs(authSiteStore)

  function orgRoute<T extends RouteName>(
    descriptor: OrgRouteDescriptor<T>,
    orgSlug?: string,
  ) {
    const slug = orgSlug ?? currentSlug.value
    return {
      name: descriptor.name,
      params: { org: slug, ...descriptor.params },
    } as RouteNamedMap[T]
  }

  const routes = {
    shifts: () => orgRoute({ name: '/(app)/[org]/schedule/shifts/'}),
    shiftsActivity: () => orgRoute({ name: '/(app)/[org]/schedule/activity/'}),
    shiftsCalendar: () => orgRoute({ name: '/(app)/[org]/schedule/calendar/'}),
    shiftDetail: (shiftId: ShiftID) =>
      orgRoute({ name: '/(app)/[org]/schedule/shifts/[id]/', params: { id: shiftId } }),
    shiftDetailEmployees: (shiftId: ShiftID) =>
      orgRoute({ name: '/(app)/[org]/schedule/shifts/[id]/employees', params: { id: shiftId } }),
    shiftDetailActivity: (shiftId: ShiftID) =>
        orgRoute({ name: '/(app)/[org]/schedule/shifts/[id]/activity', params: { id: shiftId } }),
    shiftWizard: () => orgRoute({ name: '/(app)/[org]/schedule/shifts.wizard' }),

    broadcasts: () => orgRoute({ name: '/(app)/[org]/people/broadcasts/' }),
    broadcastCreate: () => orgRoute({ name: '/(app)/[org]/people/broadcasts.create' }),
    broadcastDetail: (broadcastId: BroadcastID) =>
      orgRoute({ name: '/(app)/[org]/people/broadcasts/[id]/', params: { id: broadcastId } }),

    timeOff: () => orgRoute({ name: '/(app)/[org]/people/time-off/' }),
    timeOffCreate: () => orgRoute({ name: '/(app)/[org]/people/time-off.create' }),
    timeOffDetail: (timeOffId: TimeOffRequestID) =>
      orgRoute({ name: '/(app)/[org]/people/time-off/[id]/', params: { id: timeOffId } }),

    employees: () => orgRoute({ name: '/(app)/[org]/people/employees/' }),
    employeeCreate: () => orgRoute({ name: '/(app)/[org]/people/employees.create' }),
    employeeDetail: (employeeId: EmployeeID) =>
      orgRoute({ name: '/(app)/[org]/people/employees/[id]/', params: { id: employeeId } }),

    buildings: () => orgRoute({ name: '/(app)/[org]/settings/(organizations)/buildings' }),
    buildingCreate: () => orgRoute({ name: '/(app)/[org]/settings/buildings.create' }),
    buildingDetail: (buildingId: BuildingID) =>
      orgRoute({ name: '/(app)/[org]/settings/buildings/[id]/', params: { id: buildingId } }),

    skills: () => orgRoute({ name: '/(app)/[org]/settings/(organizations)/skills' }),
    skillCreate: () => orgRoute({ name: '/(app)/[org]/settings/skills.create' }),
    skillDetail: (skillId: SkillID) =>
      orgRoute({ name: '/(app)/[org]/settings/skills/[id]/', params: { id: skillId } }),

    groups: () => orgRoute({ name: '/(app)/[org]/settings/groups/' }),
    groupCreate: () => orgRoute({ name: '/(app)/[org]/settings/groups.create' }),
    groupDetail: (groupId: GroupID) =>
      orgRoute({ name: '/(app)/[org]/settings/groups/[id]/', params: { id: groupId } }),

    jobTitles: () => orgRoute({ name: '/(app)/[org]/settings/(organizations)/job-titles' }),
    jobRoles: () => orgRoute({ name: '/(app)/[org]/settings/(organizations)/job-roles' }),

    reasons: () => orgRoute({ name: '/(app)/[org]/settings/(organizations)/shift-reasons' }),
    reasonCreate: () => orgRoute({ name: '/(app)/[org]/settings/shift-reasons.create' }),
    reasonDetail: (reasonId: ShiftReasonID) =>
      orgRoute({ name: '/(app)/[org]/settings/shift-reasons/[id]', params: { id: reasonId } }),

    settingsBusiness: () => orgRoute({ name: '/(app)/[org]/settings/(organizations)/' }),
    settingsBusinessShiftPresets: () => orgRoute({ name: '/(app)/[org]/settings/(organizations)/shift-presets' }),
    settingsBusinessSubscription: () => orgRoute({ name: '/(app)/[org]/settings/(organizations)/subscription' }),

    notifications: () => orgRoute({ name: '/(app)/[org]/notifications/' }),

    billingPlans: () => orgRoute({ name: '/(app)/[org]/(billing)/subscription/checkout' }),

    onboarding: (stepId: string) => orgRoute({ name: '/(app)/[org]/onboarding/[stepId]', params: { stepId }})
  }

  return {
    orgRoute,
    routes,
  }
}
